
import React, { useState } from 'react';
import MasterLicenseLayout from '@/components/masterlicense/MasterLicenseLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { DialogContent, DialogDescription, DialogHeader, DialogTitle, Dialog } from '@/components/ui/dialog';
import { Plus } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMasterLicense } from '@/hooks/useMasterLicense';
import { verifySuperAdminLicense } from '@/utils/licenseService';
import { mockSuperAdmins } from '@/utils/mockData';

// Import the new components
import SuperAdminHeader from '@/components/masterlicense/SuperAdminHeader';
import AssignLicenseForm, { superAdminFormSchema, SuperAdminFormValues } from '@/components/masterlicense/AssignLicenseForm';
import SuperAdminLicenseTable from '@/components/masterlicense/SuperAdminLicenseTable';
import VerificationResults from '@/components/masterlicense/VerificationResults';

const MasterLicenseSuperAdmins = () => {
  const {
    isLoading,
    licenseKeys,
    superAdminLicenses,
    searchQuery,
    setSearchQuery,
    isAssigningLicense,
    handleAssignLicense,
    handleLogout
  } = useMasterLicense();
  
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [filteredSuperAdmins, setFilteredSuperAdmins] = useState(superAdminLicenses);
  const [isVerifying, setIsVerifying] = useState<string | null>(null);
  const [verificationResults, setVerificationResults] = useState<{[key: string]: {
    isValid: boolean;
    message?: string;
    timestamp: Date;
  }}>({});
  
  React.useEffect(() => {
    if (!searchQuery) {
      setFilteredSuperAdmins(superAdminLicenses);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = superAdminLicenses.filter(license => {
        const key = licenseKeys.find(k => k.id === license.licenseKeyId);
        return (
          key?.superAdminName?.toLowerCase().includes(query) ||
          license.domain.toLowerCase().includes(query)
        );
      });
      setFilteredSuperAdmins(filtered);
    }
  }, [searchQuery, superAdminLicenses, licenseKeys]);
  
  const form = useForm<SuperAdminFormValues>({
    resolver: zodResolver(superAdminFormSchema),
    defaultValues: {
      superAdminId: '',
      superAdminName: '',
      licenseKeyId: '',
    },
  });
  
  const onSuperAdminSelect = (superAdminId: string) => {
    const superAdmin = mockSuperAdmins.find(sa => sa.id === superAdminId);
    if (superAdmin) {
      form.setValue('superAdminName', superAdmin.name);
    }
  };
  
  const onSubmit = async (data: SuperAdminFormValues) => {
    try {
      await handleAssignLicense({
        superAdminId: data.superAdminId,
        superAdminName: data.superAdminName,
        licenseKeyId: data.licenseKeyId
      });
      setCreateDialogOpen(false);
      form.reset();
    } catch (error) {
      console.error('Failed to assign license:', error);
    }
  };
  
  const handleVerifyLicense = async (superAdminId: string) => {
    setIsVerifying(superAdminId);
    try {
      const result = await verifySuperAdminLicense(superAdminId);
      setVerificationResults({
        ...verificationResults,
        [superAdminId]: {
          isValid: result.isValid,
          message: result.message,
          timestamp: new Date()
        }
      });
    } catch (error) {
      console.error('Error verifying license:', error);
    } finally {
      setIsVerifying(null);
    }
  };
  
  const unassignedSuperAdmins = mockSuperAdmins.filter(sa => 
    !superAdminLicenses.some(license => license.superAdminId === sa.id)
  );
  
  const unassignedLicenseKeys = licenseKeys.filter(key => 
    !key.superAdminId
  );
  
  return (
    <MasterLicenseLayout
      isLoading={isLoading}
      onLogout={handleLogout}
      userRole="Master Admin"
      activeTab="super-admins"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <SuperAdminHeader 
          onCreateClick={() => setCreateDialogOpen(true)}
          isDisabled={unassignedLicenseKeys.length === 0 || unassignedSuperAdmins.length === 0}
        />
        
        <Card>
          <CardHeader>
            <CardTitle>Super Admin Licenses</CardTitle>
            <CardDescription>Manage license keys assigned to Super Admins</CardDescription>
          </CardHeader>
          <CardContent>
            <SuperAdminLicenseTable 
              filteredSuperAdmins={filteredSuperAdmins}
              licenseKeys={licenseKeys}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              isVerifying={isVerifying}
              handleVerifyLicense={handleVerifyLicense}
            />
          </CardContent>
        </Card>
        
        <VerificationResults 
          verificationResults={verificationResults}
          superAdminLicenses={superAdminLicenses}
          licenseKeys={licenseKeys}
        />
        
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Assign License to Super Admin</DialogTitle>
              <DialogDescription>
                Assign a license key to a Super Admin
              </DialogDescription>
            </DialogHeader>
            
            <AssignLicenseForm 
              form={form}
              onSubmit={onSubmit}
              isAssigningLicense={isAssigningLicense}
              unassignedSuperAdmins={unassignedSuperAdmins}
              unassignedLicenseKeys={unassignedLicenseKeys}
              onSuperAdminSelect={onSuperAdminSelect}
            />
          </DialogContent>
        </Dialog>
      </div>
    </MasterLicenseLayout>
  );
};

export default MasterLicenseSuperAdmins;
